import React from 'react';
import { Typography, Box, Grid, Card, Stack, Chip, Divider, Button as MuiButton } from '@mui/material';
import { useAppContext } from '../context/AppContext';
import { FileText, Rocket, Calendar, TrendingUp, TrendingDown, Zap, GalleryHorizontal, Image as ImageIcon, ArrowRight, Volume2, Sparkles, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
    const { user, posts, usage, isLoading } = useAppContext();
    const navigate = useNavigate();

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '3px solid #e2e8f0',
                    borderTopColor: '#4f46e5',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                    }
                }} />
            </Box>
        );
    }

    const recentPosts = posts.slice(0, 5);
    const creditsLeft = usage.planLimit - usage.generationsUsed;

    // --- Real trend calculations ---

    // Card 1: Posts week-over-week growth
    const now = new Date();
    const startOfThisWeek = new Date(now);
    startOfThisWeek.setDate(now.getDate() - now.getDay()); // Sunday
    startOfThisWeek.setHours(0, 0, 0, 0);
    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

    const postsThisWeek = posts.filter(p => new Date(p.createdAt) >= startOfThisWeek).length;
    const postsLastWeek = posts.filter(p => {
        const d = new Date(p.createdAt);
        return d >= startOfLastWeek && d < startOfThisWeek;
    }).length;

    let postsTrend: string;
    let postsTrendUp: boolean;
    if (postsLastWeek === 0) {
        postsTrend = postsThisWeek > 0 ? 'New this week' : 'No posts yet';
        postsTrendUp = postsThisWeek > 0;
    } else {
        const pct = Math.round(((postsThisWeek - postsLastWeek) / postsLastWeek) * 100);
        postsTrend = `${pct >= 0 ? '+' : ''}${pct}% vs last wk`;
        postsTrendUp = pct >= 0;
    }

    // Card 2: Monthly usage %
    const usagePct = usage.planLimit > 0 ? Math.round((usage.generationsUsed / usage.planLimit) * 100) : 0;
    const usageTrend = usagePct >= 100 ? 'Limit reached' : usagePct >= 75 ? 'High usage' : 'On track';
    const usageTrendUp = usagePct < 75;

    // Card 3: Credits remaining
    const creditsPercent = usage.planLimit > 0 ? (creditsLeft / usage.planLimit) * 100 : 100;
    const creditsTrend = creditsLeft === 0 ? 'Upgrade now' : creditsLeft <= 2 ? 'Running low' : 'Healthy';
    const creditsTrendUp = creditsLeft > 2;

    return (
        <Box>
            {/* Welcome Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={800} sx={{ color: '#111827', mb: 1, letterSpacing: '-0.02em' }}>
                        Welcome back, {user?.name.split(' ')[0] || 'Creator'} ✨
                    </Typography>
                    <Typography variant="body1" color="#64748b">
                        Your personal content studio is ready. What are we creating today?
                    </Typography>
                </Box>
                <MuiButton
                    variant="outlined"
                    startIcon={<Calendar size={18} />}
                    sx={{
                        color: '#334155',
                        borderColor: '#e2e8f0',
                        bgcolor: 'white',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2,
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                    }}
                >
                    {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </MuiButton>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <StatCard
                        icon={<FileText size={20} color="#4f46e5" />}
                        iconBg="#e0e7ff"
                        title="Total Posts Generated"
                        value={posts.length.toLocaleString()}
                        trend={postsTrend}
                        trendUp={postsTrendUp}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <StatCard
                        icon={<Zap size={20} color="#4f46e5" />}
                        iconBg="#e0e7ff"
                        title="Monthly Usage"
                        value={`${usage.generationsUsed}/${usage.planLimit}`}
                        trend={`${usagePct}% used · ${usageTrend}`}
                        trendUp={usageTrendUp}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <StatCard
                        icon={<Rocket size={20} color="#4f46e5" />}
                        iconBg="#e0e7ff"
                        title="Credits Left"
                        value={creditsLeft.toLocaleString()}
                        trend={creditsTrend}
                        trendUp={creditsTrendUp}
                    />
                </Grid>
            </Grid>

            {/* Main Content Area */}
            <Grid container spacing={3}>
                {/* Left Column: Recent Generations */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight={700} color="#111827">
                                Recent Generations
                            </Typography>
                            <Typography
                                component="button"
                                variant="body2"
                                fontWeight={600}
                                color="#4f46e5"
                                onClick={() => navigate('/history')}
                                sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}
                            >
                                View all
                            </Typography>
                        </Box>

                        <Divider />

                        {recentPosts.length > 0 ? (
                            <>
                                {/* Table Header */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px', px: 3, py: 2, bgcolor: '#f8fafc' }}>
                                    <Typography variant="caption" fontWeight={700} color="#64748b" letterSpacing="0.05em">TOPIC</Typography>
                                    <Typography variant="caption" fontWeight={700} color="#64748b" letterSpacing="0.05em">TYPE</Typography>
                                    <Typography variant="caption" fontWeight={700} color="#64748b" letterSpacing="0.05em">DATE</Typography>
                                    <Typography variant="caption" fontWeight={700} color="#64748b" letterSpacing="0.05em" align="right">ACTION</Typography>
                                </Box>
                                <Divider />
                                <Stack divider={<Divider />}>
                                    {recentPosts.map((post) => (
                                        <GenerationRow
                                            key={post.id}
                                            topic={post.topic}
                                            type={post.postType}
                                            date={new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            icon={post.postType === 'Carousel Post' ? <GalleryHorizontal size={16} color="#4f46e5" /> : post.postType === 'Caption Post' ? <ImageIcon size={16} color="#4f46e5" /> : <Zap size={16} color="#4f46e5" />}
                                            iconBg="#e0e7ff"
                                            onView={() => navigate(`/dashboard/post/${post.id}`)}
                                        />
                                    ))}
                                </Stack>
                            </>
                        ) : (
                            <Box sx={{ p: 8, textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                    <FileText size={32} color="#94a3b8" />
                                </Box>
                                <Typography variant="h6" fontWeight={700} color="#1e293b" gutterBottom>
                                    No generations yet
                                </Typography>
                                <Typography variant="body2" color="#64748b" sx={{ mb: 3, maxWidth: 300 }}>
                                    Your generated posts will appear here once you start creating content.
                                </Typography>
                                <MuiButton
                                    variant="contained"
                                    onClick={() => navigate('/generate')}
                                    sx={{ bgcolor: '#4f46e5', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                                >
                                    Create First Post
                                </MuiButton>
                            </Box>
                        )}
                    </Card>
                </Grid>

                {/* Right Column: Quick Actions & Links */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={3}>
                        {/* Create New Post Card */}
                        <Card elevation={0} sx={{ borderRadius: 4, bgcolor: '#4f46e5', color: 'white', p: 4 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                Create New Post
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6, mb: 3 }}>
                                Let our AI generate high-converting social media content for you in seconds.
                            </Typography>
                            <MuiButton
                                variant="contained"
                                startIcon={<Sparkles size={18} />}
                                fullWidth
                                onClick={() => navigate('/generate')}
                                sx={{
                                    bgcolor: 'white',
                                    color: '#4f46e5',
                                    fontWeight: 700,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#f8fafc' }
                                }}
                            >
                                Open Generator
                            </MuiButton>
                        </Card>

                        {/* Quick Links Card */}
                        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={700} color="#111827" gutterBottom sx={{ mb: 2 }}>
                                Quick Links
                            </Typography>
                            <Stack spacing={2}>
                                <QuickLinkRow onClick={() => navigate('/dashboard/campaigns')} icon={<Volume2 size={18} color="#4f46e5" />} label="New Campaign" />
                                <QuickLinkRow onClick={() => navigate('/dashboard/insights')} icon={<Zap size={18} color="#4f46e5" />} label="Weekly Insights" />
                            </Stack>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

// Subcomponents

function StatCard({ icon, iconBg, title, value, trend, trendUp }: any) {
    return (
        <Card elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {icon}
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    {trendUp
                        ? <TrendingUp size={16} color="#10b981" />
                        : <TrendingDown size={16} color="#ef4444" />}
                    <Typography variant="caption" fontWeight={700} color={trendUp ? '#10b981' : '#ef4444'}>
                        {trend}
                    </Typography>
                </Stack>
            </Box>
            <Typography variant="caption" color="#64748b" fontWeight={600} gutterBottom>
                {title}
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#111827" sx={{ letterSpacing: '-0.02em' }}>
                {value}
            </Typography>
        </Card>
    );
}

function GenerationRow({ topic, type, date, icon, iconBg, onView }: any) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px', px: 3, py: 2.5, alignItems: 'center', '&:hover': { bgcolor: '#f8fafc' } }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {icon}
                </Box>
                <Typography variant="body2" fontWeight={600} color="#334155" noWrap sx={{ pr: 2 }}>
                    {topic}
                </Typography>
            </Stack>
            <Box>
                <Chip
                    label={type}
                    size="small"
                    sx={{
                        bgcolor: '#f1f5f9',
                        color: '#1e293b',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        borderRadius: 1.5,
                        px: 0.5
                    }}
                />
            </Box>
            <Box>
                <Typography variant="caption" color="#64748b" sx={{ display: 'block', lineHeight: 1.3 }}>
                    {date}
                </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
                <Typography
                    component="button"
                    variant="body2"
                    fontWeight={700}
                    color="#4f46e5"
                    onClick={onView}
                    sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}
                >
                    View
                </Typography>
            </Box>
        </Box>
    );
}

function QuickLinkRow({ icon, label, onClick }: any) {
    return (
        <Box
            component="button"
            onClick={onClick}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: 3, border: '1px solid #f1f5f9', bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' }, cursor: 'pointer', width: '100%' }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5}>
                {icon}
                <Typography variant="body2" fontWeight={600} color="#334155">
                    {label}
                </Typography>
            </Stack>
            <ArrowRight size={16} color="#94a3b8" />
        </Box>
    );
}
